import React, { ComponentPropsWithRef, FC } from "react";

import cn from "@/utils/cn";

import AnchorLink from "../AnchorLink/AnchorLink";
import "./button.css";

type ButtonType = "button" | "submit" | "reset";

interface Props extends ComponentPropsWithRef<"button"> {
  size?: ButtonSize;
  variant?: ButtonVariant;
  theme?: ButtonTheme;
  children?: React.ReactElement | React.ReactElement[] | string;
  type?: ButtonType;
  href?: string | { href: string; query: string };
  rounded?: boolean | undefined;
  customClass?: string;
  download?: boolean;
  target?: string;
  query?: string | null | undefined;
  poneNumberCta?: string | boolean | Function | null | undefined;
  isDisabled?: boolean | undefined;
}

export enum ButtonVariant {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  TERTIARY = "tertiary",
}

export enum ButtonSize {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
}

export enum ButtonTheme {
  BLACK = "black",
  WHITE = "white",
  GOLD = "gold",
  NONE = "none",
}

/* BUTTON SHARED */
const shared = "btn";
/* VARIANTS */
const buttonVariants = {
  primary: {
    black: "btn-primary-black",
    white: "btn-primary-white",
    gold: "btn-primary-gold",
    none: "btn",
  },
  secondary: {
    black: "btn-secondary-black",
    white: "btn-secondary-white",
    gold: "btn-secondary-gold",
    none: "btn",
  },
  tertiary: {
    black: "btn-tertiary-black",
    white: "btn-tertiary-white",
    gold: "btn-tertiary-gold",
    none: "btn",
  },
};
/* SIZES */
const sizes = {
  small: "btn-small",
  medium: "btn-medium",
  large: "btn-large",
};

const getThemeClass = (variant: string, theme: string) => {
  const v = Object.values(ButtonVariant).filter((el) => el === variant);
  const t = Object.values(ButtonTheme).filter((el) => el === theme);
  return buttonVariants[v[0]][t[0]];
};

const getSizeClass = (size: string) => {
  const sizeValue = Object.values(ButtonSize).filter((el) => el === size);
  return sizes[sizeValue[0]];
};

const Button: FC<Props> = ({
  size = ButtonSize.MEDIUM,
  variant = ButtonVariant.PRIMARY,
  theme = ButtonTheme.NONE,
  type,
  children,
  href,
  rounded = false,
  customClass,
  query,
  isDisabled,
  poneNumberCta = false,
  ...rest
}) => {
  return href ? (
    <>
      <AnchorLink
        href={typeof href === "string" ? href : href.href}
        query={!!query ? query : typeof href !== "string" ? href?.query : ""}
        className={cn(shared, getSizeClass(size), getThemeClass(variant, theme), rounded && "round", customClass)}
        poneNumberCta={poneNumberCta}
        {...rest}
      >
        {children}
      </AnchorLink>
    </>
  ) : (
    <button
      type={type || "button"}
      className={cn(shared, getSizeClass(size), getThemeClass(variant, theme), rounded && "round", customClass)}
      {...rest}
      disabled={isDisabled}
    >
      {children}
    </button>
  );
};

export default Button;
